// AGC: SorteioRoulette - Full-screen roulette overlay for Instagram livestream draws
// AGC: Phases: ready → spinning → revealed → confirmed

import React, { useState, useEffect, useRef, useCallback } from 'react'
import { X, Instagram, ExternalLink, RotateCcw, Check } from 'lucide-react'
import { useAuth } from '../../hooks/useAuth'
import { getBackendUrl } from '../../utils/backendUrl'
import type { Sorteio } from '../../types/sorteio'

// AGC: Draw winner response shape from backend
interface DrawWinnerResponse {
  success: boolean
  winner: {
    number: string
    userName: string
    userLastName: string
    instagramHandle?: string
    userId: string
  }
}

// AGC: Component phases for the roulette flow
type RoulettePhase = 'ready' | 'spinning' | 'revealed' | 'confirmed'

// AGC: Number of digit boxes in the roulette display
const DIGIT_COUNT = 5

// AGC: Interval for cycling random digits during spin (ms)
const SPIN_INTERVAL_MS = 40

// AGC: Total spin duration before all digits reveal simultaneously (ms)
// AGC: One-shot reveal avoids the "we're picking the number live" optics of digit-by-digit
const SPIN_DURATION_MS = 2500

// AGC: Generate a random single digit 0–9
function randomDigit(): string {
  return String(Math.floor(Math.random() * 10))
}

// AGC: Pad a number string to DIGIT_COUNT with leading zeros
function padNumber(num: string): string {
  return num.padStart(DIGIT_COUNT, '0')
}

interface Props {
  sorteio: Sorteio
  onClose: () => void
}

export const SorteioRoulette: React.FC<Props> = ({ sorteio, onClose }) => {
  const { getAuthToken } = useAuth()

  // AGC: Current display phase
  const [phase, setPhase] = useState<RoulettePhase>('ready')

  // AGC: The 5 digits currently displayed on screen
  const [digits, setDigits] = useState<string[]>(Array(DIGIT_COUNT).fill('0'))

  // AGC: Which digit indices have stopped spinning (index → true)
  const [stoppedDigits, setStoppedDigits] = useState<boolean[]>(Array(DIGIT_COUNT).fill(false))

  // AGC: The winner data returned by backend
  const [winner, setWinner] = useState<DrawWinnerResponse['winner'] | null>(null)

  // AGC: Channels through which the winner was notified (populated after confirm-winner response)
  // AGC: Possible values: 'firestore' (in-app), 'fcm' (push), 'whatsapp'
  const [notifiedChannels, setNotifiedChannels] = useState<string[]>([])

  // AGC: Numbers excluded from draw (skipped winners)
  const [excludeNumbers, setExcludeNumbers] = useState<string[]>([])

  // AGC: Error message to display
  const [error, setError] = useState<string | null>(null)

  // AGC: Whether an async action is in-flight
  const [actionLoading, setActionLoading] = useState(false)

  // AGC: Ref to the spin interval so we can clear it on unmount
  const spinIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // AGC: Ref to digit-stop timeouts so we can clear them on unmount
  const stopTimeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([])

  // AGC: Build auth headers for API calls
  const authHeaders = useCallback(async (): Promise<Record<string, string>> => {
    const token = await getAuthToken()
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    }
  }, [getAuthToken])

  // AGC: Clear all timers (spin interval + stop timeouts)
  const clearAllTimers = useCallback(() => {
    if (spinIntervalRef.current !== null) {
      clearInterval(spinIntervalRef.current)
      spinIntervalRef.current = null
    }
    stopTimeoutsRef.current.forEach(clearTimeout)
    stopTimeoutsRef.current = []
  }, [])

  // AGC: Cleanup timers on unmount
  useEffect(() => {
    return () => {
      clearAllTimers()
    }
  }, [clearAllTimers])

  // AGC: Start the spin animation and fetch winner from backend
  const handleStartSpin = useCallback(async () => {
    setPhase('spinning')
    setError(null)
    setStoppedDigits(Array(DIGIT_COUNT).fill(false))

    // AGC: Start random digit cycling
    spinIntervalRef.current = setInterval(() => {
      setDigits((prev) =>
        prev.map((_, i) => {
          // AGC: Keep stopped digits at their final value
          return i < DIGIT_COUNT ? randomDigit() : prev[i]
        })
      )
    }, SPIN_INTERVAL_MS)

    // AGC: Fetch winner from backend while digits spin
    let winnerNumber = '00000'
    try {
      const headers = await authHeaders()
      const res = await fetch(`${getBackendUrl()}/api/v1/sorteios/${sorteio.id}/draw`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ excludeNumbers }),
      })
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error(body.error ?? `${res.status} ${res.statusText}`)
      }
      const data: DrawWinnerResponse = await res.json()
      if (!data.success) throw new Error('Draw returned success=false')
      setWinner(data.winner)
      winnerNumber = padNumber(data.winner.number)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
      clearAllTimers()
      setPhase('ready')
      return
    }

    // AGC: Reveal all digits at once after SPIN_DURATION_MS — no digit-by-digit stop
    const finalDigits = winnerNumber.split('')
    const revealAll = setTimeout(() => {
      if (spinIntervalRef.current !== null) {
        clearInterval(spinIntervalRef.current)
        spinIntervalRef.current = null
      }
      setDigits(finalDigits)
      setStoppedDigits(Array(DIGIT_COUNT).fill(true))
      // AGC: Brief pause on locked number before revealing the winner card
      const revealTimeout = setTimeout(() => setPhase('revealed'), 600)
      stopTimeoutsRef.current.push(revealTimeout)
    }, SPIN_DURATION_MS)
    stopTimeoutsRef.current.push(revealAll)
  }, [authHeaders, clearAllTimers, excludeNumbers, sorteio.id])

  // AGC: Confirm winner — POST to confirm-winner endpoint
  const handleConfirm = useCallback(async () => {
    if (!winner) return
    setActionLoading(true)
    setError(null)
    try {
      const headers = await authHeaders()
      const res = await fetch(
        `${getBackendUrl()}/api/v1/sorteios/${sorteio.id}/confirm-winner`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({
            userId: winner.userId,
            number: winner.number,
            userName: `${winner.userName ?? ''} ${winner.userLastName ?? ''}`.trim(),
            instagramHandle: winner.instagramHandle ?? '',
          }),
        }
      )
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error(body.error ?? `${res.status} ${res.statusText}`)
      }
      // AGC: Backend returns notifiedChannels — capture for display in confirmed phase
      const body = (await res.json().catch(() => ({}))) as { notifiedChannels?: string[] }
      setNotifiedChannels(Array.isArray(body.notifiedChannels) ? body.notifiedChannels : [])
      setPhase('confirmed')
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setActionLoading(false)
    }
  }, [authHeaders, sorteio.id, winner])

  // AGC: Skip winner — POST to skip-winner endpoint, then reset to ready
  const handleSkip = useCallback(async () => {
    if (!winner) return
    setActionLoading(true)
    setError(null)
    try {
      const headers = await authHeaders()
      const res = await fetch(
        `${getBackendUrl()}/api/v1/sorteios/${sorteio.id}/skip-winner`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({
            number: winner.number,
            name: `${winner.userName ?? ''} ${winner.userLastName ?? ''}`.trim(),
            instagram: winner.instagramHandle ?? '',
            reason: 'skipped_in_roulette',
          }),
        }
      )
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error(body.error ?? `${res.status} ${res.statusText}`)
      }
      // AGC: Add skipped number to exclusion list for next draw
      setExcludeNumbers((prev) => [...prev, winner.number])
      setWinner(null)
      setDigits(Array(DIGIT_COUNT).fill('0'))
      setStoppedDigits(Array(DIGIT_COUNT).fill(false))
      setPhase('ready')
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setActionLoading(false)
    }
  }, [authHeaders, sorteio.id, winner])

  // AGC: Full name helper
  const winnerFullName = winner
    ? `${winner.userName} ${winner.userLastName}`.trim()
    : ''

  // AGC: Instagram URL helper
  const instagramUrl = winner?.instagramHandle
    ? `https://instagram.com/${winner.instagramHandle.replace(/^@/, '')}`
    : null

  return (
    // AGC: Full-screen black overlay, above everything
    <div className="fixed inset-0 z-[100] bg-black flex flex-col items-center justify-center select-none">
      {/* AGC: Close button — top-right, subtle */}
      {phase !== 'spinning' && (
        <button
          onClick={onClose}
          className="absolute top-5 right-5 p-2 text-gray-600 hover:text-gray-400 transition-colors"
          aria-label="Fechar"
        >
          <X className="w-7 h-7" />
        </button>
      )}

      {/* ─────────────────────────── PHASE: ready ─────────────────────────── */}
      {phase === 'ready' && (
        <div className="flex flex-col items-center gap-8 px-8 text-center">
          {/* AGC: Title */}
          <h1 className="text-5xl font-black tracking-widest text-yellow-400 uppercase">
            Sorteio
          </h1>

          {/* AGC: Prize name */}
          <p className="text-2xl font-semibold text-white">{sorteio.prize}</p>

          {/* AGC: Excluded numbers notice */}
          {excludeNumbers.length > 0 && (
            <p className="text-sm text-gray-500">
              {excludeNumbers.length}{' '}
              {excludeNumbers.length === 1 ? 'número excluído' : 'números excluídos'}
            </p>
          )}

          {/* AGC: Error display */}
          {error && (
            <p className="text-red-400 text-sm max-w-sm">{error}</p>
          )}

          {/* AGC: Main draw button */}
          <button
            onClick={handleStartSpin}
            className="px-16 py-5 bg-yellow-400 hover:bg-yellow-300 text-black text-2xl font-black uppercase tracking-widest rounded-2xl shadow-2xl transition-colors"
          >
            Sortear
          </button>
        </div>
      )}

      {/* ────────────────────────── PHASE: spinning ───────────────────────── */}
      {phase === 'spinning' && (
        <div className="flex flex-col items-center gap-12 px-8">
          {/* AGC: Title stays visible during spin */}
          <h1 className="text-5xl font-black tracking-widest text-yellow-400 uppercase">
            Sorteio
          </h1>

          {/* AGC: Digit boxes row */}
          <div className="flex items-center gap-3">
            {digits.map((digit, idx) => {
              const stopped = stoppedDigits[idx]
              return (
                <div
                  key={idx}
                  className={[
                    'h-28 w-20 flex items-center justify-center rounded-2xl',
                    'text-6xl font-black font-mono transition-all duration-300',
                    stopped
                      ? 'bg-yellow-500 text-black shadow-[0_0_30px_rgba(234,179,8,0.6)] scale-110'
                      : 'bg-gray-800 text-white',
                  ].join(' ')}
                >
                  {digit}
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* ─────────────────────────── PHASE: revealed ──────────────────────── */}
      {/* AGC: Keep the SAME digit boxes from the spin visible — they now show the winner's */}
      {/* AGC: digits. Winner name/instagram + action buttons appear below. No layout switch, */}
      {/* AGC: no flash of a "different" number, no re-render of the number display. */}
      {phase === 'revealed' && winner && (
        <div className="flex flex-col items-center gap-10 px-8 text-center max-w-lg w-full">
          {/* AGC: Title */}
          <h1 className="text-5xl font-black tracking-widest text-yellow-400 uppercase">
            Sorteio
          </h1>

          {/* AGC: Same digit boxes — all locked on winner digits */}
          <div className="flex items-center gap-3">
            {padNumber(winner.number)
              .split('')
              .map((digit, idx) => (
                <div
                  key={idx}
                  className="h-28 w-20 flex items-center justify-center rounded-2xl text-6xl font-black font-mono bg-yellow-500 text-black shadow-[0_0_30px_rgba(234,179,8,0.6)] scale-110"
                >
                  {digit}
                </div>
              ))}
          </div>

          {/* AGC: Winner card — name + instagram, number lives in the boxes above */}
          <div className="w-full rounded-2xl border border-yellow-500/50 bg-gray-900 dark:bg-slate-900 p-6 flex flex-col items-center gap-3">
            {/* AGC: GANHADOR label */}
            <p className="text-xs font-bold tracking-[0.3em] text-yellow-400 uppercase">
              Ganhador
            </p>

            {/* AGC: Full name */}
            <p className="text-3xl font-bold text-white leading-tight">
              {winnerFullName}
            </p>

            {/* AGC: Instagram link — opens in new tab */}
            {instagramUrl && winner.instagramHandle && (
              <a
                href={instagramUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-pink-400 hover:text-pink-300 transition-colors text-sm font-medium"
              >
                <Instagram className="w-4 h-4" />
                @{winner.instagramHandle.replace(/^@/, '')}
                <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>

          {/* AGC: Error display */}
          {error && (
            <p className="text-red-400 text-sm">{error}</p>
          )}

          {/* AGC: Action buttons */}
          <div className="flex gap-4 w-full">
            {/* AGC: Confirm winner */}
            <button
              onClick={handleConfirm}
              disabled={actionLoading}
              className="flex-1 flex items-center justify-center gap-2 py-4 bg-brand-primary dark:bg-brand-light hover:bg-brand-dark dark:hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed text-white text-lg font-bold rounded-2xl shadow-lg transition-colors"
            >
              <Check className="w-5 h-5" />
              Confirmar Vencedor
            </button>

            {/* AGC: Skip and draw again */}
            <button
              onClick={handleSkip}
              disabled={actionLoading}
              className="flex-1 flex items-center justify-center gap-2 py-4 bg-error-600 dark:bg-error-500 hover:bg-error-700 dark:hover:bg-error-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-lg font-bold rounded-2xl shadow-lg transition-colors"
            >
              <RotateCcw className="w-5 h-5" />
              Sortear Novamente
            </button>
          </div>
        </div>
      )}

      {/* ─────────────────────────── PHASE: confirmed ─────────────────────── */}
      {phase === 'confirmed' && winner && (
        <div className="flex flex-col items-center gap-8 px-8 text-center">
          {/* AGC: Celebration emoji */}
          <span className="text-8xl" role="img" aria-label="celebração">
            🎉
          </span>

          {/* AGC: Winner name in green */}
          <p className="text-3xl font-bold text-green-400">{winnerFullName}</p>

          {/* AGC: Lucky number */}
          <p className="text-xl font-mono text-yellow-400 tracking-widest">
            #{padNumber(winner.number)}
          </p>

          {/* AGC: Notification delivery summary — shows which channels reached the winner */}
          {notifiedChannels.length > 0 ? (
            <div className="flex flex-col items-center gap-2 max-w-sm">
              <p className="text-xs font-bold tracking-[0.25em] text-gray-400 uppercase">
                Vencedor Notificado
              </p>
              <div className="flex flex-wrap justify-center gap-2">
                {notifiedChannels.includes('firestore') && (
                  <span className="px-3 py-1 text-xs font-semibold bg-blue-900/50 text-blue-300 rounded-full border border-blue-700">
                    🔔 No app
                  </span>
                )}
                {notifiedChannels.includes('fcm') && (
                  <span className="px-3 py-1 text-xs font-semibold bg-purple-900/50 text-purple-300 rounded-full border border-purple-700">
                    📱 Push
                  </span>
                )}
                {notifiedChannels.includes('whatsapp') && (
                  <span className="px-3 py-1 text-xs font-semibold bg-green-900/50 text-green-300 rounded-full border border-green-700">
                    💬 WhatsApp
                  </span>
                )}
              </div>
            </div>
          ) : (
            <p className="text-xs text-gray-500 max-w-sm">
              Vencedor confirmado, mas nenhum canal de notificação disponível.
            </p>
          )}

          {/* AGC: Close button */}
          <button
            onClick={onClose}
            className="px-12 py-4 bg-yellow-400 hover:bg-yellow-300 text-black text-xl font-black uppercase tracking-widest rounded-2xl shadow-2xl transition-colors"
          >
            Fechar
          </button>
        </div>
      )}
    </div>
  )
}
